import React, { useEffect, useState } from 'react'
import { useRequest } from 'hooks/useRequest'
import AnimatedSpan from 'containers/animatedSpan/AnimatedSpan'
import DefaultSelect from 'components/formElements/DefaultSelect'
import Spinner from 'components/loader/Spinner'
import { Dropdown } from 'primereact/dropdown'
import Button from 'components/formElements/Button'
import moment from 'moment/moment'
import { MdCheckCircle, MdRadioButtonUnchecked } from 'react-icons/md'
import useToast from 'hooks/useToast'
import { getPermission, userInfo } from 'utils/Auth'
import { Dialog } from 'primereact/dialog'
import ApprovalContent from 'components/modalContent/ApprovalContent'
import { useDispatch, useSelector } from 'react-redux'
import { closeApprovalModal, openApprovalModal } from 'redux/features/general/generalSlice'
import { DataTable } from 'primereact/datatable'
import Icons from 'assets/icons'
import { Column } from 'primereact/column'
import { roleIds } from 'utils/Constants'
import { Calendar } from 'primereact/calendar'

const CrmReminderModal = ({ reminder, crmEvents, users, companies, onReminderChange, onSave, onCancel, isEdit, onCompanyChange }) => {
    const handleChange = (field, value) => {
        // Müşteri değiştiğinde ilgili etkinlikleri yükle
        if (field === 'crmManagementId') {
            onCompanyChange(value)
        }
        onReminderChange({ ...reminder, [field]: value })
    }
    
    return (
        <div className='w-100'>
            <div className='w-100 d-flex flex-column gap-3'>
                <div className='row'>
                    <div className='col-md-12'>
                        <div className='form-control'>
                            <label className='form-control-label'>Müşteri *</label>
                            <Dropdown
                                value={reminder?.crmManagementId || null}
                                onChange={e => handleChange('crmManagementId', e.value)}
                                options={companies || []}
                                optionLabel='label'
                                optionValue='value'
                                placeholder='Müşteri seçin'
                                filter
                                filterLocale='tr'
                                filterInputAutoFocus={false}
                                className='w-100'
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <div className='form-control'>
                            <label className='form-control-label'>Tarih - Saat *</label>
                            <Calendar
                                value={reminder?.dateAt ? moment(reminder.dateAt).toDate() : null}
                                onChange={e => handleChange('dateAt', e.value)}
                                placeholder='Tarih ve saat seçin'
                                dateFormat='dd/mm/yy'
                                showTime
                                hourFormat='24'
                                className='w-100'
                                showIcon
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <div className='form-control'>
                            <label className='form-control-label'>İlgili Kişiler *</label>
                            <DefaultSelect
                                isMulti
                                handleChange={(e) => handleChange('userIds', e ? e.map(u => u.value) : [])}
                                selectOptions={users}
                                value={reminder.userIds ? 
                                    users.filter(u => reminder.userIds.includes(u.value)) : []}
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <div className='form-control'>
                            <label className='form-control-label'>Not *</label>
                            <textarea
                                className='default-input'
                                rows='4'
                                value={reminder?.note || ''}
                                onChange={e => handleChange('note', e.target.value)}
                                placeholder='Hatırlatıcı notu...'
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <div className='form-control'>
                            <label className='form-control-label'>Durum</label>
                            <Dropdown
                                value={reminder?.crmReminderStatus ?? 0}
                                onChange={e => handleChange('crmReminderStatus', e.value)}
                                options={[
                                    { label: 'Açık', value: 0 },
                                    { label: 'Tamamlandı', value: 1 }
                                ]}
                                optionLabel='label'
                                optionValue='value'
                                placeholder='Durum seçin'
                                className='w-100'
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <div className='form-control'>
                            <label className='form-control-label'>İlişkili Etkinlik</label>
                            <Dropdown
                                value={reminder?.crmManagementEventId || null}
                                onChange={e => handleChange('crmManagementEventId', e.value)}
                                options={crmEvents || []}
                                optionLabel='label'
                                optionValue='value'
                                placeholder='Etkinlik seçin'
                                filter
                                filterLocale='tr'
                                filterInputAutoFocus={false}
                                className='w-100'
                                showClear
                            />
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-12 d-flex justify-content-end gap-3'>
                        <Button className='btn-danger' title='İptal' handleClick={onCancel} />
                        <Button className='btn-success' title={isEdit ? 'Güncelle' : 'Kaydet'} handleClick={onSave} />
                    </div>
                </div>
            </div>
        </div>
    )
}

const CrmReminders = () => {
    const [request] = useRequest()
    const { showToast } = useToast()
    const dispatch = useDispatch()
    const { general } = useSelector((state) => state)
    const { approvalModal } = general
    const [crmReminders, setCrmReminders] = useState([])
    const [loading, setLoading] = useState(false)
    const [users, setUsers] = useState([])
    const [allCrmCompanies, setAllCrmCompanies] = useState([])
    const [crmEvents, setCrmEvents] = useState([])
    const [upsertReminderModal, setUpsertReminderModal] = useState({ open: false, id: null, reminder: {} })
    const [filterOptions, setFilterOptions] = useState({ 
        status: null, 
        dateRange: null, 
        createdBy: null,
        users: []
    })

    useEffect(() => {
        getCrmReminders()
        getInitialData()
        getAllCrmCompanies()
        getAllCrmEvents()
    }, [])

    // Modal açıldığında ve crmManagementId varsa etkinlikleri yükle
    useEffect(() => {
        if (upsertReminderModal.open && upsertReminderModal.reminder?.crmManagementId) {
            getAllCrmEvents(upsertReminderModal.reminder.crmManagementId)
        }
    }, [upsertReminderModal.open, upsertReminderModal.reminder?.crmManagementId])

    const getCrmReminders = () => {
        setLoading(true)
        request('/CrmManagement/GetAllCrmReminders', { method: 'GET' })
            .then(res => {
                if (res.data.ok) {
                    setCrmReminders(res.data.result)
                } else {
                    showToast('Hatırlatıcılar yüklenirken hata oluştu', 'error')
                }
            })
            .catch(error => {
                console.error('Error fetching CRM reminders:', error)
                showToast('Hatırlatıcılar yüklenirken hata oluştu', 'error')
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const getInitialData = () => {
        // Get users (Account Managers, Project Managers, Admins)
        request('/Account/GetAllUsers', { method: 'GET', params: {
            isDeleteFilter: true
        } }).then((res) => {
            if (res.data.ok) {
                setUsers(res.data.result
                    .filter(r => 
                        r.roles.map(role => role.id).includes(roleIds.accountManager) || 
                        r.roles.map(role => role.id).includes(roleIds.projectManager) || 
                        r.roles.map(role => role.id).includes(roleIds.admin)
                    )
                    .map((u) => ({ label: `${u.firstName} ${u.lastName}`, value: u.id })))
            }
        }).catch(error => {
            console.error('Error fetching users:', error)
        })
    }

    const getAllCrmCompanies = () => {
        request('/CrmManagement/GetAllCrmManagements', { method: 'GET' })
            .then(res => {
                if (res.data.ok) {
                    const formattedCompanies = res.data.result.map(company => ({
                        label:company?.serviceProvider != null ? (company.crmCompanyName || company.crmCompany?.name || 'Bilinmeyen') + (company?.serviceProvider === 1 ? ' - Odito' : ' - Nuevo') : company.crmCompanyName || company.crmCompany?.name || 'Bilinmeyen',
                        value: company.id
                    }))
                    setAllCrmCompanies(formattedCompanies)
                } else {
                    showToast('Müşteriler yüklenirken hata oluştu', 'error')
                }
            })
            .catch(error => {
                console.error('Error fetching CRM companies:', error)
                showToast('Müşteriler yüklenirken hata oluştu', 'error')
            })
    }

    const getAllCrmEvents = (crmManagementId = null) => {
        const params = crmManagementId ? { crmManagementId } : {}
        
        request('/CrmManagement/GetCrmManagementEvents', { method: 'GET', params })
            .then(res => {
                if (res.data.ok) {
                    const formattedEvents = res.data.result
                        .filter(event => 
                            event.crmManagementActivityTypeName === 'Arama' || 
                            event.crmManagementActivityTypeName === 'Toplantı' || 
                            event.crmManagementActivityTypeName === 'Teams Görüşmesi'
                        )
                        .map(event => {
                            const eventDate = moment(event?.eventDate).format('DD/MM/YYYY')
                            const projectName = event?.leadManagement?.projectName || 'İlgili Lead Yok'
                            const activityType = event?.crmManagementActivityTypeName || 'Etkinlik'
                            
                            return {
                                label: `${eventDate} - ${projectName} - ${activityType}`,
                                value: event.id,
                                fullData: event
                            }
                        })
                    setCrmEvents(formattedEvents)
                }
            })
            .catch(error => {
                console.error('Error fetching CRM events:', error)
            })
    }

    const handleCompanyChangeInModal = (crmManagementId) => {
        if (crmManagementId) {
            getAllCrmEvents(crmManagementId)
        } else {
            setCrmEvents([])
        }
    }

    const deleteButton = (deleteData) => {
        return (
            <Icons.deleteNotFill
                onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteRow(deleteData)
                }}
                size={20}
                className='text-danger cursor-pointer'
            />
        )
    }

    const handleDeleteRow = (deleteData) => {
        dispatch(openApprovalModal({ 
            open: true, 
            content: 'Hatırlatıcıyı silmek istediğinize emin misiniz?', 
            deleteData,
            requestType: 'deleteReminder'
        }))
    }

    const handleApprovalModalApproved = () => {
        if (approvalModal.requestType === 'deleteReminder') {
            handleCloseApprovalModal()
            handleDeleteCrmReminder(approvalModal.deleteData)
            return
        }

        if (approvalModal.requestType === 'reminderStatus') {
            handleCloseApprovalModal()
            handleUpdateReminderStatus(
                {...approvalModal.reminderData, userIds: approvalModal.reminderData.users?.map(u => u.id) || []}, 
                approvalModal.newStatus
            )
            return
        }
    }

    const handleCloseApprovalModal = () => {
        dispatch(closeApprovalModal())
    }

    const handleDeleteCrmReminder = (reminderData) => {
        request('/CrmManagement/DeleteCrmReminder', {
            method: 'DELETE',
            params: { id: reminderData.id }
        }).then((res) => {
            if (res.data.ok) {
                showToast('Hatırlatıcı Silindi', 'success')
                getCrmReminders()
            } else {
                showToast('Silme işlemi sırasında hata oluştu', 'error')
            }
        }).catch(error => {
            showToast('Silme işlemi sırasında hata oluştu', 'error')
            console.error('Error deleting CRM reminder:', error)
        })
    }

    const handleAddCrmReminder = () => {
        if (!upsertReminderModal.reminder?.crmManagementId) {
            showToast('Müşteri Seçin', 'error')
            return
        }
        if (!upsertReminderModal.reminder?.dateAt) {
            showToast('Tarih-Saat Seçin', 'error')
            return
        }
        if (!upsertReminderModal.reminder?.userIds || upsertReminderModal.reminder.userIds.length === 0) {
            showToast('En az bir kişi seçin', 'error')
            return
        }
        if (!upsertReminderModal.reminder?.note || upsertReminderModal.reminder.note.trim() === '') {
            showToast('Not Girin', 'error')
            return
        }

        const postData = {
            id: upsertReminderModal.id || 0,
            dateAt: upsertReminderModal.reminder.dateAt ? 
                moment(upsertReminderModal.reminder.dateAt).format('YYYY-MM-DDTHH:mm:ss') : null,
            note: upsertReminderModal.reminder.note || "",
            crmReminderStatus: upsertReminderModal.reminder.crmReminderStatus ?? 0,
            crmManagementEventId: upsertReminderModal.reminder.crmManagementEventId || null,
            userIds: upsertReminderModal.reminder.userIds,
            crmManagementId: upsertReminderModal.reminder.crmManagementId
        }

        request('/CrmManagement/UpsertCrmReminder', {
            method: 'POST',
            data: postData
        }).then((res) => {
            if (res.data.ok) {
                showToast(upsertReminderModal.id ? 'Hatırlatıcı Güncellendi' : 'Hatırlatıcı Eklendi', 'success')
                setUpsertReminderModal({ open: false, id: null, reminder: {} })
                getCrmReminders()
            } else {
                showToast('İşlem sırasında hata oluştu', 'error')
            }
        }).catch(error => {
            showToast('İşlem sırasında hata oluştu', 'error')
            console.error('Error saving CRM reminder:', error)
        })
    }

    const handleReminderStatusChange = (reminder, newStatus) => {
        const statusMessage = newStatus === 1 ? 'Tamamlandı olarak işaretlensin' : 'Açık olarak işaretlensin'
        const confirmMessage = `Hatırlatıcı ${statusMessage} mi?`
        
        dispatch(openApprovalModal({ 
            open: true, 
            content: confirmMessage, 
            requestType: 'reminderStatus',
            reminderData: reminder,
            newStatus: newStatus
        }))
    }

    const handleUpdateReminderStatus = (reminder, newStatus) => {
        const updateData = {
            id: reminder.id,
            dateAt: reminder.dateAt,
            note: reminder.note || "",
            crmReminderStatus: newStatus,
            crmManagementEventId: reminder.crmManagementEventId,
            crmManagementId: reminder.crmManagementId,
            userIds: reminder.userIds || []
        }

        request('/CrmManagement/UpsertCrmReminder', {
            method: 'POST',
            data: updateData
        }).then((res) => {
            if (res.data.ok) {
                const successMessage = newStatus === 1 ? 'Hatırlatıcı Tamamlandı olarak güncellendi' : 'Hatırlatıcı Açık olarak güncellendi'
                showToast(successMessage, 'success')
                getCrmReminders()
            } else {
                showToast('Durum güncelleme sırasında hata oluştu', 'error')
            }
        }).catch(error => {
            showToast('Durum güncelleme sırasında hata oluştu', 'error')
            console.error('Error updating reminder status:', error)
        })
    }

    // Filtreleme fonksiyonu
    const filteredReminders = crmReminders.filter((reminder) => {
        let pass = true

        // Durum filtresi
        if (filterOptions.status !== null && filterOptions.status !== undefined) {
            pass = pass && reminder.crmReminderStatus === filterOptions.status
        }

        // Tarih aralığı filtresi
        if (filterOptions.dateRange && filterOptions.dateRange.length === 2) {
            const [startDate, endDate] = filterOptions.dateRange
            if (startDate && endDate && reminder.dateAt) {
                const reminderDate = new Date(reminder.dateAt)
                const reminderDateOnly = new Date(reminderDate.getFullYear(), reminderDate.getMonth(), reminderDate.getDate())
                const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
                const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
                
                pass = pass && (reminderDateOnly >= startDateOnly && reminderDateOnly <= endDateOnly)
            }
        }

        // Ekleyen filtresi
        if (filterOptions.createdBy) {
            pass = pass && reminder.createdBy === filterOptions.createdBy
        }

        // İlgili kişiler filtresi
        if (filterOptions.users && filterOptions.users.length > 0) {
            const selectedUserIds = filterOptions.users.map(u => u.value)
            const reminderUserIds = reminder.users?.map(u => u.id) || []
            const hasMatchingUser = reminderUserIds.some(userId => selectedUserIds.includes(userId))
            pass = pass && hasMatchingUser
        }

        return pass
    })

    const handleFilterChange = (value, field) => {
        setFilterOptions(prev => ({ ...prev, [field]: value }))
    }

    return (
        <AnimatedSpan className='page-container d-flex flex-column gap-4'>
            <div className='page-header'>
                <h2>Hatırlatıcılar</h2>
            </div>

            {loading ? (
                <div className='d-flex w-100 justify-content-center'>
                    <Spinner size={3} />
                </div>
            ) : (
                <div className='row'>
                        <DataTable
                            emptyMessage='Hiç Kayıt Bulunamadı'
                            responsiveLayout='stack'
                            className='col-md-12 p-1'
                            dataKey='id'
                            header={
                                <div className='d-flex flex-column gap-3'>
                                    <div className='row'>
                                        <div className='col-md-3'>
                                            <div style={{position: 'relative'}} className='form-control'>
                                                <Calendar
                                                    value={filterOptions.dateRange}
                                                    onChange={(e) => handleFilterChange(e.value, 'dateRange')}
                                                    selectionMode="range"
                                                    readOnlyInput
                                                    placeholder="Tarih aralığı seçin..."
                                                    showIcon
                                                    dateFormat="dd/mm/yy"
                                                    className="w-100"
                                                />
                                                {filterOptions.dateRange && filterOptions.dateRange.length > 0 && (
                                                    <button 
                                                        type="button"
                                                        onClick={() => handleFilterChange(null, 'dateRange')}
                                                        title="Tarih filtresini temizle"
                                                        style={{
                                                            right: '60px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            background: 'none',
                                                            border: 'none',
                                                            fontSize: '14px',
                                                            color: '#6c757d',
                                                            cursor: 'pointer',
                                                            padding: '2px 4px',
                                                            zIndex: 1000,
                                                            position: 'absolute'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.color = '#dc3545'}
                                                        onMouseLeave={(e) => e.target.style.color = '#6c757d'}
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className='col-md-2'>
                                            <DefaultSelect
                                                handleChange={(e) => handleFilterChange(e ? e.value : null, 'status')}
                                                value={filterOptions.status !== null ? 
                                                    { label: filterOptions.status === 1 ? 'Tamamlandı' : 'Açık', value: filterOptions.status } : null}
                                                placeholder='Durum'
                                                isSearchable
                                                isClearable
                                                selectOptions={[
                                                    { label: 'Tamamlandı', value: 1 },
                                                    { label: 'Açık', value: 0 }
                                                ]}
                                            />
                                        </div>
                                        <div className='col-md-3'>
                                            <DefaultSelect
                                                handleChange={(e) => handleFilterChange(e ? e.label : null, 'createdBy')}
                                                value={filterOptions.createdBy ? 
                                                    users.find(u => u.label === filterOptions.createdBy) : null}
                                                placeholder='Ekleyen'
                                                isSearchable
                                                isClearable
                                                selectOptions={users}
                                            />
                                        </div>
                                        <div className='col-md-3'>
                                            <DefaultSelect
                                                handleChange={(e) => handleFilterChange(e || [], 'users')}
                                                value={filterOptions.users}
                                                placeholder='İlgili Kişiler'
                                                isSearchable
                                                isMulti
                                                isClearable
                                                selectOptions={users}
                                            />
                                        </div>
                                        <div className='col-md-1 d-flex justify-content-end'>
                                            {getPermission('CrmManagement.update') && (
                                                <Button 
                                                    className='btn-primary btn-sm' 
                                                    title='Hatırlatıcı Ekle' 
                                                    handleClick={() => {
                                                        const currentUserId = userInfo ? Number(userInfo['jti']) : null;
                                                        setUpsertReminderModal({ 
                                                            open: true, 
                                                            id: null, 
                                                            reminder: currentUserId ? { userIds: [currentUserId] } : {} 
                                                        })
                                                    }} 
                                                    icon={<Icons.plus />} 
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            }
                            paginator
                            alwaysShowPaginator={false}
                            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                            currentPageReportTemplate="Toplam {totalRecords} kayıttan {first} - {last} arasındakiler gösteriliyor"
                            rows={25}
                            value={filteredReminders}
                            rowClassName={`cursor-pointer table-hover`}
                            cellClassName={`text-sm p-2`}
                            onRowClick={e => {
                                if (e.originalEvent.target.className.includes('p-button-icon') || 
                                    e.originalEvent.target.tagName === 'svg' ||
                                    e.originalEvent.target.tagName === 'path') return
                                if (getPermission('CrmManagement.update') && e.data.id) {
                                    setUpsertReminderModal({ 
                                        open: true, 
                                        id: e.data.id, 
                                        reminder: {...e.data, userIds: e.data?.users?.map(u => u.id) || []} 
                                    })
                                }
                            }}
                        >
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='crmManagement.newCrmCompanyName'
                                style={{ width: 'auto' }}
                                header='Müşteri'
                                sortable
                                body={(rowData) => rowData.crmManagement?.serviceProvider != null ? (rowData.crmManagement?.newCrmCompanyName || 
                                    rowData.crmManagement?.crmCompany?.name || '-') + (rowData.crmManagement?.serviceProvider === 1 ? ' - Odito' : ' - Nuevo') : rowData.crmManagement?.newCrmCompanyName || 
                                    rowData.crmManagement?.crmCompany?.name || '-'}
                            />
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='dateAt'
                                style={{ width: 'auto' }}
                                header='Hatırlatıcı Tarihi'
                                sortable
                                body={(rowData) => rowData.dateAt && moment(rowData.dateAt).format('DD/MM/YYYY HH:mm')}
                            />
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='users'
                                style={{ width: 'auto' }}
                                header='İlgili Kişiler'
                                body={(rowData) => {
                                    if (rowData.users && rowData.users.length > 0) {
                                        const userNames = rowData.users
                                            .map(user => (`${user.firstName} ${user.lastName}`))
                                            .join(', ');
                                        return userNames || '-';
                                    }
                                    return '-';
                                }}
                            />
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='note'
                                style={{ maxWidth: '300px' }}
                                header='Hatırlatıcı Notu'
                                body={(rowData) => rowData.note && (
                                    <span 
                                        style={{ 
                                            maxWidth: '100%', 
                                            padding: '5px', 
                                            margin: '0',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            lineHeight: '1.2em',
                                            maxHeight: '2.8em'
                                        }}
                                    >
                                        {rowData.note}
                                    </span>
                                ) || '-'}
                            />
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='crmReminderStatus'
                                style={{ width: 'auto' }}
                                header='Durum'
                                body={(rowData) => (
                                    <span className={rowData.crmReminderStatus === 1 ? 'text-success' : 'text-warning'}>
                                        <strong>{rowData.crmReminderStatus === 1 ? 'Tamamlandı' : 'Açık'}</strong>
                                    </span>
                                )}
                            />
                            <Column
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold'
                                field='crmManagementEventId'
                                style={{ width: 'auto' }}
                                header='İlgili Etkinlik'
                                body={(rowData) => {
                                    if (rowData.crmManagementEvent) {
                                        const event = rowData.crmManagementEvent
                                        const eventDate = moment(event.eventDate).format('DD/MM/YYYY')
                                        const projectName = event.leadManagement?.projectName || 'İlgili Lead Yok'
                                        const activityType = event?.crmActivityChannel?.name || 'Etkinlik'
                                        return `${eventDate} - ${projectName} - ${activityType}`
                                    }
                                    return '-';
                                }}
                            />
                            <Column
                                field='action'
                                headerClassName='text-bold bg-primary text-default'
                                className='text-semi-bold pl-0'
                                style={{ width: '5rem' }}
                                body={(rowData) => (
                                    <span className='d-flex gap-1 align-items-center'>
                                        {getPermission('CrmManagement.update') && (
                                            rowData.crmReminderStatus === 1 ? (
                                                <MdCheckCircle
                                                    className='text-success cursor-pointer'
                                                    style={{fontSize: '1.2rem'}}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleReminderStatusChange(rowData, 0);
                                                    }}
                                                    title="Açık Olarak İşaretle"
                                                />
                                            ) : (
                                                <MdRadioButtonUnchecked
                                                    className='text-warning cursor-pointer'
                                                    style={{fontSize: '1.2rem'}}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleReminderStatusChange(rowData, 1);
                                                    }}
                                                    title="Tamamlandı Olarak İşaretle"
                                                />
                                            )
                                        )}
                                        {getPermission('CrmManagement.update') && deleteButton(rowData)}
                                    </span>
                                )}
                            />
                        </DataTable>
                </div>
            )}

            <Dialog draggable={false} visible={approvalModal.open} onHide={handleCloseApprovalModal}>
                <ApprovalContent handleClose={handleCloseApprovalModal} handleApproved={handleApprovalModalApproved} />
            </Dialog>
            <Dialog 
                style={{ width: '600px', maxWidth: '100%' }} 
                draggable={false} 
                header={upsertReminderModal.id ? 'Hatırlatıcı Düzenle' : 'Hatırlatıcı Ekle'} 
                visible={upsertReminderModal.open} 
                onHide={() => setUpsertReminderModal({ open: false, id: null, reminder: {} })}
            >
                <CrmReminderModal 
                    reminder={upsertReminderModal.reminder}
                    crmEvents={crmEvents}
                    users={users}
                    companies={allCrmCompanies}
                    onReminderChange={(reminder) => setUpsertReminderModal(prev => ({ ...prev, reminder }))}
                    onCompanyChange={handleCompanyChangeInModal}
                    onSave={handleAddCrmReminder}
                    onCancel={() => setUpsertReminderModal({ open: false, id: null, reminder: {} })}
                    isEdit={!!upsertReminderModal.id}
                />
            </Dialog>
        </AnimatedSpan>
    )
}

export default CrmReminders
